import { useState } from 'react';

import { useClerk } from '@clerk/react';
import type { OAuthStrategy } from '@clerk/shared/types';

import { useCustomToast } from '../../../hooks/useCustomToast';
import { APP_PAGES } from '../../../routes/constants';
import { trackGA4Event } from '../../../utils/analytics/ga4';
import { useSentryLogger } from '../../../utils/sentry';

const GA4_CATEGORY = 'User';
const GA4_ACTION = 'Social Signup';
const GA4_LABEL_GOOGLE = 'Google';
const GA4_LABEL_MICROSOFT = 'Microsoft';
const OAUTH_GOOGLE: OAuthStrategy = 'oauth_google';

export function useOAuthSignIn() {
  const { client } = useClerk();
  const { errorMessage } = useCustomToast();
  const { logException } = useSentryLogger();
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const handleSocialSignIn = async (strategy: OAuthStrategy) => {
    if (isAuthenticating) {
      return;
    }
    setIsAuthenticating(true);
    try {
      trackGA4Event({
        category: GA4_CATEGORY,
        action: GA4_ACTION,
        label:
          strategy === OAUTH_GOOGLE ? GA4_LABEL_GOOGLE : GA4_LABEL_MICROSOFT,
      });
      await client.signIn.authenticateWithRedirect({
        strategy,
        redirectUrl: APP_PAGES.SSO_CALLBACK,
        redirectUrlComplete: '/',
        continueSignIn: false,
      });
    } catch (error: unknown) {
      logException(error, 'Error login with social media', 'Login');
      if (error instanceof Error) {
        errorMessage(error.message);
      }
    } finally {
      setIsAuthenticating(false);
    }
  };

  return { handleSocialSignIn, isAuthenticating };
}
